import { BaseSyntheticEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Namespace, TFunction, useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { LEAD_GQL_QUERY_BASE } from 'components/domain/lead/useFetchLead';
import usePreAuthorizationChargeMutation from 'components/domain/order/usePreAuthorizationChargeMutation';
import FormWithProvider from 'components/forms/form/Form';
import { FormContextParts } from 'components/forms/form/Form.types';
import useModalId from 'components/modal/useModalId';
import { parseISO } from 'date-fns';
import useAppModal from 'hooks/useAppModal';
import { Lead } from 'models/Leads';
import { PreAuthorization } from 'models/securityDeposits/PreAuthorizations';
import ChargeModalFormElements from 'pages/orderView/transactions/actions/ChargeModalFormElements';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

const preAuthorizationChargeFormSchema = (
  maxAmount: number,
  t: TFunction<Namespace<'en'>>,
) =>
  Yup.object({
    amount: Yup.number()
      .positive()
      .max(
        maxAmount,
        t(
          'componentOrder.securityDepositPreAuthorization.chargeModal.maxAmountExceeded',
          { maxAmount },
        ),
      )
      .required(),
  });

type PreAuthorizationChargeFormValues = Yup.InferType<
  ReturnType<typeof preAuthorizationChargeFormSchema>
>;

interface PreAuthorizationChargeModalProps {
  lead: Lead;
  preAuthorization: Pick<
    PreAuthorization,
    'status' | 'startAt' | 'expiresAt' | 'currency' | 'remainingAmount' | 'uid'
  >;
}

const PreAuthorizationChargeModal = ({
  lead,
  preAuthorization,
}: PreAuthorizationChargeModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { closeModal } = useAppModal();
  const modalId = useModalId();
  const { mutateAsync: chargePreAuthorization } =
    usePreAuthorizationChargeMutation();

  const { uid: leadUid } = lead;

  const onCancel = () => {
    closeModal(modalId);
  };

  const onSubmit = async (
    { amount }: PreAuthorizationChargeFormValues,
    _event: BaseSyntheticEvent | undefined,
    { setError }: FormContextParts<PreAuthorizationChargeFormValues>,
  ) => {
    await chargePreAuthorization(
      {
        preAuthorizationUid: preAuthorization.uid,
        amount,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([LEAD_GQL_QUERY_BASE, leadUid]);
          closeModal(modalId);
        },
        onError: (error) => {
          setError('root', {
            message: getErrorMessage({
              apiErrorCode: error?.response?.data?.apiErrorCode,
              apiErrorMessage: error?.response?.data?.apiErrorMessage ?? '',
              baseKey:
                'componentOrder.securityDepositPreAuthorization.apiErrorCodes.',
              t,
            }) as string,
          });
          queryClient.invalidateQueries([LEAD_GQL_QUERY_BASE, leadUid]);
        },
      },
    );
  };

  const formDefaultValues = {
    amount: preAuthorization.remainingAmount,
  };

  return (
    <FormWithProvider
      defaultValues={formDefaultValues}
      noValidate
      onSubmit={onSubmit}
      resolver={preAuthorizationChargeFormSchema(
        preAuthorization.remainingAmount,
        t,
      )}
    >
      <ChargeModalFormElements
        amount={preAuthorization.remainingAmount}
        amountLabel={t(
          'componentOrder.securityDepositPreAuthorization.chargeModal.amountLabel',
        )}
        currency={preAuthorization.currency}
        onCancel={onCancel}
        rowDescription={t(
          'componentOrder.securityDepositPreAuthorization.chargeModal.descriptionRow',
          {
            startDate: parseISO(preAuthorization.startAt),
            endDate: preAuthorization.expiresAt
              ? parseISO(preAuthorization.expiresAt)
              : '-',
          },
        )}
      />
    </FormWithProvider>
  );
};

export default PreAuthorizationChargeModal;
